import { useEffect, useRef } from 'react';

/**
 * Subscribe to a global keyboard hotkey.
 *
 * Supported combos:
 *   - 'cmd+k' / 'ctrl+k'      — modifier + single key
 *   - 'cmd+\\'                 — modifier + symbol
 *   - 'shift+arrowdown'       — shift + arrow / other named key
 *
 * Cmd/Ctrl combos are always available. Shift combos respect typing
 * targets (input / textarea / contenteditable) so native Shift+Arrow text
 * selection keeps working inside fields.
 */

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

function parseCombo(combo: string): {
  modifier: 'meta' | 'ctrl' | 'shift';
  key: string;
} {
  const lower = combo.toLowerCase();
  const parts = lower.split('+').map((s) => s.trim());
  const mod = parts[0] ?? '';
  const key = parts[1] ?? '';
  const modifier =
    mod === 'cmd' ? 'meta' : mod === 'ctrl' ? 'ctrl' : mod === 'shift' ? 'shift' : 'meta';
  return { modifier, key };
}

export function useHotkey(combo: string, handler: () => void): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const { modifier, key } = parseCombo(combo);

    function onKeyDown(e: KeyboardEvent): void {
      if (modifier === 'shift' && isTypingTarget(e.target)) return;
      const matches =
        (modifier === 'meta' && e.metaKey) ||
        (modifier === 'ctrl' && e.ctrlKey) ||
        (modifier === 'shift' && e.shiftKey);
      if (matches && e.key.toLowerCase() === key) {
        e.preventDefault();
        handlerRef.current();
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [combo]);
}
